import { Component, type ErrorInfo, type ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import type { Root, RootContent } from "mdast";
import type { Plugin } from "unified";
import "katex/dist/katex.min.css";

type ParentNode = Root | Extract<RootContent, { children: unknown }>;

const remarkLiteralHtml: Plugin<[], Root> = () => (tree) => {
  const replaceHtml = (parent: ParentNode) => {
    parent.children = parent.children.map((node) => {
      if (node.type === "html") {
        return node.value.includes("\n")
          ? { type: "code", lang: "html", value: node.value }
          : { type: "inlineCode", value: node.value };
      }
      if ("children" in node && Array.isArray(node.children)) replaceHtml(node as ParentNode);
      return node;
    }) as typeof parent.children;
  };
  replaceHtml(tree);
};

type HastNode = {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const rehypeMathPresentation: Plugin = () => (tree: HastNode) => {
  const visit = (node: HastNode) => {
    const classNames = Array.isArray(node.properties?.className) ? node.properties.className : [];
    if (classNames.includes("katex-display")) {
      node.properties = { ...node.properties, role: "region", tabIndex: 0, ariaLabel: "Scrollable display equation" };
    }
    if (classNames.includes("katex-error")) {
      node.properties = { ...node.properties, role: "note" };
      node.children = [...(node.children ?? []), {
        type: "element",
        tagName: "span",
        properties: { className: ["math-error-label"] },
        children: [{ type: "text", value: " — Math could not be rendered" }],
      }];
    }
    node.children?.forEach(visit);
  };
  visit(tree);
};

type SafeDestination = { href: string; kind: "internal" | "external" | "email" };

function safeDestination(value?: string): SafeDestination | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("//")) return { href: trimmed, kind: "external" };
  if (/^(?:[/?#.]|[^:/?#]+(?:[/?#]|$))/.test(trimmed) && !/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)) {
    return { href: trimmed, kind: "internal" };
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") return { href: trimmed, kind: "external" };
    if (parsed.protocol === "mailto:") return { href: trimmed, kind: "email" };
  } catch {
    return null;
  }
  return null;
}

function SafeLink({ href, children, image = false }: { href?: string | undefined; children: ReactNode; image?: boolean }) {
  const destination = safeDestination(href);
  if (!destination) return <span className="notes-unsafe-link">{children}</span>;
  if (destination.kind === "internal") return <a href={destination.href}>{children}</a>;
  if (destination.kind === "email") return <a href={destination.href}>{children}<span className="sr-only"> (opens email application)</span></a>;
  return <a href={destination.href} target="_blank" rel="noopener noreferrer" className={image ? "notes-image-link" : undefined}>{children}<ExternalLink aria-hidden="true" size={12} className="notes-external-icon" /><span className="sr-only"> (opens in a new window)</span></a>;
}

const markdownComponents: Components = {
  a: ({ href, children }) => <SafeLink href={href}>{children}</SafeLink>,
  img: ({ src, alt }) => <SafeLink href={typeof src === "string" ? src : undefined} image>{alt?.trim() || "Image link"}</SafeLink>,
  input: ({ type, checked }) => type === "checkbox"
    ? <input type="checkbox" checked={checked} disabled tabIndex={-1} aria-label={`Read-only Markdown task: ${checked ? "Completed" : "Open"}`} />
    : null,
  table: ({ node: _node, children, ...props }) => <div className="notes-scroll-region" role="region" tabIndex={0} aria-label="Scrollable Notes table"><table {...props}>{children}</table></div>,
  pre: ({ node: _node, children, ...props }) => <pre {...props} className="notes-scroll-region" role="region" tabIndex={0} aria-label="Scrollable Notes code block">{children}</pre>,
  h1: ({ node: _node, ...props }) => <h4 {...props} />,
  h2: ({ node: _node, ...props }) => <h5 {...props} />,
  h3: ({ node: _node, ...props }) => <h6 {...props} />,
  h4: ({ node: _node, ...props }) => <h6 {...props} aria-level={6} />,
  h5: ({ node: _node, ...props }) => <h6 {...props} aria-level={6} />,
  h6: ({ node: _node, ...props }) => <h6 {...props} aria-level={6} />,
};

class NotesRenderBoundary extends Component<{ source: string; children: ReactNode; noun: string }, { failed: boolean }> {
  state = { failed: false };
  componentDidCatch(_error: Error, _info: ErrorInfo) { this.setState({ failed: true }); }
  componentDidUpdate(previous: Readonly<{ source: string; children: ReactNode; noun: string }>) {
    if (this.state.failed && previous.source !== this.props.source) this.setState({ failed: false });
  }
  render() {
    if (this.state.failed) return <div className="notes-render-fallback" role="note"><p>{this.props.noun} could not be rendered. The source is shown below.</p><pre>{this.props.source}</pre></div>;
    return this.props.children;
  }
}

export function TaskNotesMarkdown({ source, label = "Notes", fallbackNoun = "Notes" }: { source: string; label?: string; fallbackNoun?: string }) {
  return <NotesRenderBoundary source={source} noun={fallbackNoun}><div className="notes-markdown" aria-label={label}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks, remarkMath, remarkLiteralHtml]}
      rehypePlugins={[[rehypeKatex, { throwOnError: false, trust: false, strict: "warn" }], rehypeMathPresentation]}
      components={markdownComponents}
      urlTransform={(url) => url}
    >{source}</ReactMarkdown>
  </div></NotesRenderBoundary>;
}

export { safeDestination };
